// Seamless looping audio engine module - Optimized postMessage implementation
import { validatePlayOptions } from '../../src/core/audio-engine/audio-math.js';
import { AUDIO_CONSTANTS } from '../../src/config/audio-constants.js';
import { setupSeamlessPlayback } from '../../src/core/audio-engine/setup-helpers-seamless.js';
import { ChannelDataSource } from '../utils/channel-data-source.js';
import { AudioCacheManager } from '../managers/audio-cache-manager.js';
import { TempoController } from '../managers/tempo-controller.js';
import { WorkletFactory } from '../managers/worklet-factory.js';
import { PlaybackStateManager } from '../managers/playback-state-manager.js';
import { AudioNodeManager } from '../managers/audio-node-manager.js';
import type { AudioEngineConfig, AudioEngine, PlayOptions } from '../../src/types/audio.js';
import type { AudioTrackLength } from '../../src/types/audio-length.js';

export const createAudioEngine = (config: AudioEngineConfig): AudioEngine => {
  const {
    audioContext: providedAudioContext,
    createDevice,
    crossfadeTime = AUDIO_CONSTANTS.DEFAULT_CROSSFADE_TIME_MS,
    playbackAudioWorkletUrl,
    promisedWebAssemblyModule,
    signalsmithStretchAudioWorkletUrl,
    workerUrl,
    sharedTempoController
  } = config;

  let initialPlaybackTempo = config.playbackTempo;
  const audioContext =
    providedAudioContext || new AudioContext({ sampleRate: AUDIO_CONSTANTS.SAMPLE_RATE });

  // Initialize managers
  const cacheManager = new AudioCacheManager(audioContext);
  // Use shared tempo controller if provided, otherwise create local one
  const tempoController = sharedTempoController || new TempoController(audioContext);

  if (sharedTempoController) {
    console.log('🔗 Using shared TempoController for synchronized timing');
  } else {
    console.log('⚠️ Using local TempoController - tracks may drift');
  }
  const workletFactory = new WorkletFactory({
    audioContext,
    playbackAudioWorkletUrl,
    signalsmithStretchAudioWorkletUrl,
    promisedWebAssemblyModule,
    workerUrl
  });
  const playbackStateManager = new PlaybackStateManager();
  const audioNodeManager = new AudioNodeManager({ audioContext, createDevice });

  let device: any = null;
  let isPlaying = false;
  const channelDataManagersToStop = new Set<any>();
  const playbackTempoUpdateFunctions = new Set<Function>();

  // Note: Using optimized seamless postMessage approach
  console.log('🚀 Using optimized seamless audio engine');

  // Load worklets
  const promisedAudioContext = workletFactory.loadWorkletModules();

  const queue = async (url: string): Promise<void> => {
    console.log('📥 Queueing audio file:', url);

    // Use cache manager to load and cache the audio
    const cached = await cacheManager.getOrLoad(url);

    // Ensure audio buffer is loaded
    await cached.source.loadAudioBuffer();

    console.log('✅ Audio file queued and ready:', url);
  };

  const stop = async (url?: string): Promise<boolean> => {
    if (url) {
      // Stop specific track - not implemented in seamless version
      return false;
    }

    // Stop all
    isPlaying = false;
    playbackStateManager.clearGlobalTiming();
    channelDataManagersToStop.forEach(manager => manager.stop());
    channelDataManagersToStop.clear();
    playbackTempoUpdateFunctions.clear();
    return true;
  };

  const setup = async () => {
    await promisedAudioContext;
    await audioNodeManager.setup();

    if (!device) {
      device = await createDevice(audioContext);
      audioNodeManager.connectToDevice(device.node);
      console.log('🔊 Connected audio graph: worklet → gain → analyser & device → destination');
    }

    return { device, audioContext };
  };

  const play = async (options: PlayOptions) => {
    // Validate options
    const validation = validatePlayOptions(options);
    if (!validation.isValid) {
      throw new Error(`Invalid play options: ${validation.errors.join(', ')}`);
    }

    const { url, timeSignature = 4 } = options;

    // Ensure worklets are loaded
    await promisedAudioContext;
    await setup();

    // Get cached audio data (this includes metadata extraction)
    console.log('⏳ Loading cached audio data for:', url);
    const cached = await cacheManager.getOrLoad(url);
    const { source: channelDataSource, metadata } = cached;

    // Ensure audio buffer is loaded to get playback length
    await channelDataSource.loadAudioBuffer();

    // Create track length from cached metadata (single source of truth)
    const trackLength = {
      syncLength: metadata.originalSamples, // Use cached metadata for sync boundaries
      playbackLength: channelDataSource.getDecodedLength(), // Use decoded buffer for reading
      metadata: {
        encoderDelay: metadata.encoderDelay,
        endPadding: metadata.endPadding,
        fileLength: metadata.originalSamples + metadata.encoderDelay + metadata.endPadding
      }
    };

    console.log('✅ Track length created from cached metadata for:', url);

    console.log(`🔍 AudioTrackLength for ${url}:`, {
      'syncLength (musical boundary)': trackLength.syncLength,
      'playbackLength (decoded buffer)': trackLength.playbackLength,
      encoderDelay: trackLength.metadata.encoderDelay,
      endPadding: trackLength.metadata.endPadding,
      fileLength: trackLength.metadata.fileLength
    });

    const samplesPerBeat = (60 * audioContext.sampleRate) / 100; // 100 BPM at 48kHz = 28,800 samples/beat
    const trackLengthInBeats = Math.round(trackLength.syncLength / samplesPerBeat);

    // Calculate tempo from sync length (metadata-driven)
    const calculatedTempo =
      (60 * trackLengthInBeats * audioContext.sampleRate) / trackLength.syncLength;

    const tempoRatio = initialPlaybackTempo / calculatedTempo;

    console.log(`🎵 Tempo calculation for ${url}:`);
    console.log(`  - trackLengthInBeats: ${trackLengthInBeats}`);
    console.log(`  - syncLength: ${trackLength.syncLength}`);
    console.log(`  - sampleRate: ${audioContext.sampleRate}`);
    console.log(`  - calculatedTempo: ${calculatedTempo}`);
    console.log(`  - initialPlaybackTempo: ${initialPlaybackTempo}`);
    console.log(`  - tempoRatio: ${tempoRatio}`);

    // Set tempo ratio
    tempoController.setTempoRatio(tempoRatio);

    // Create worklet pair with clear track length terminology
    const isTempoSynced = true; // Always tempo-synced with metadata-driven approach
    const workletPair = await workletFactory.createWorkletPair(isTempoSynced, trackLength);

    // Connect audio chain
    workletPair.playbackWorklet.connect(workletPair.stretchWorklet);
    workletPair.stretchWorklet.connect(audioNodeManager.getInputNode());

    // Connect tempo control
    tempoController.connectToWorklets(
      workletPair.playbackWorklet.parameters.get('playbackRate'),
      workletPair.stretchWorklet.parameters.get('playbackRate')
    );
    tempoController.start();

    // Set global timing
    const startTime = options.when || audioContext.currentTime;
    playbackStateManager.setGlobalTiming(startTime, timeSignature);

    // Start seamless playback

    // Use optimized seamless playback (postMessage-based)
    await setupSeamlessPlayback(
      {
        url,
        timeSignature
      },
      channelDataSource,
      workletPair.playbackWorklet,
      workletPair.stretchWorklet,
      trackLength
    );

    console.log('✅ Using optimized seamless playback');

    // Ensure audio context is running
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
      console.log('🔊 Resumed audio context');
    }

    // Start playback
    console.log(
      `▶️ Starting simple playback at: ${startTime} (current: ${audioContext.currentTime})`
    );
    workletPair.playbackWorklet.port.postMessage({ type: 'start', when: startTime });

    // Update tempo function
    const updateTempo = () => {
      const newTempoRatio = initialPlaybackTempo / calculatedTempo;
      tempoController.setTempoRatio(newTempoRatio);
      console.log(`🎵 Updated tempo ratio to: ${newTempoRatio} (tempo: ${initialPlaybackTempo})`);
    };
    playbackTempoUpdateFunctions.add(updateTempo);

    isPlaying = true;
    playbackStateManager.addActiveChannel(0);

    // Store reference for stopping
    channelDataManagersToStop.add({
      stop: () => {
        workletPair.playbackWorklet.port.postMessage('stop');
        if (workletPair.worker) {
          workletPair.worker.postMessage(workletPair.workerId);
          workletPair.worker.terminate();
        }
        tempoController.stop();
        workletFactory.cleanupWorkletPair(workletPair);
        playbackTempoUpdateFunctions.delete(updateTempo);
        playbackStateManager.removeActiveChannel(0);

        // Cleanup resources (no special cleanup needed for seamless playback)
      }
    });
  };

  return {
    get state() {
      return isPlaying ? 'playing' : 'paused';
    },
    get playbackTempo() {
      return initialPlaybackTempo;
    },
    set playbackTempo(value: number) {
      initialPlaybackTempo = value;
      playbackTempoUpdateFunctions.forEach(fn => fn());
    },
    get currentCount() {
      return playbackStateManager.calculateCurrentCount(audioContext, initialPlaybackTempo);
    },
    get gain() {
      return audioNodeManager.getGain();
    },
    set gain(value: number) {
      audioNodeManager.setGain(value);
    },
    get rmsLevel() {
      return audioNodeManager.getRMSLevel();
    },
    audioContext,
    setup,
    queue,
    play,
    stop,
    resume: async () => {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
    },
    suspend: async () => {
      if (audioContext.state === 'running') {
        await audioContext.suspend();
      }
    },
    setMetadata: (metadata: any) => {
      // Metadata is handled per-file in the seamless approach
      console.log('Metadata update:', metadata);
    },
    teardown: () => {
      stop().then(() => {
        if (device) {
          device.node.disconnect();
        }
        audioNodeManager.teardown();
        tempoController.stop();
        cacheManager.clear();
      });
    },
    // Expose cache manager for accessing metadata
    getCachedAudio: (url: string) => cacheManager.get(url),
    // Expose analyser for backwards compatibility
    analyserNode: audioNodeManager.getAnalyserNode()
  };
};
